import React, { useState, useEffect, useRef } from 'react';
import { API_BASE } from '../../services/api'

interface GooglePlacesAutocompleteProps {
  value: string;
  onChange: (address: string, coordinates?: { lat: number; lng: number }) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  id?: string;
  error?: string;
  onClear?: () => void;
}

interface AutocompletePrediction {
  place_id: string;
  description: string;
  structured_formatting?: {
    main_text: string;
    secondary_text: string;
  };
}

const GooglePlacesAutocomplete: React.FC<GooglePlacesAutocompleteProps> = ({
  value,
  onChange,
  placeholder = 'Enter address...',
  disabled = false,
  className = '',
  id,
  error,
  onClear,
}) => {
  const [predictions, setPredictions] = useState<AutocompletePrediction[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const fetchPredictions = async (input: string) => {
    // Skip if insufficient input
    if (!input || input.length <= 2) {
      setPredictions([]);
      setIsDropdownOpen(false);
      return;
    }

    setIsLoading(true);

    try {
      // Use server proxy for Google Places API

      // Build server API URL - use production API URL
      const serverUrl = API_BASE;
      const url = `${serverUrl}/api/places/autocomplete?input=${encodeURIComponent(input)}&types=establishment&components=country:my`;

      const response = await fetch(url, {
        signal: AbortSignal.timeout(5000), // 5 second timeout
        headers: {
          'Accept': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Server API returned status: ${response.status}`);
      }

      const data = await response.json();

      if (data.statusCode === 200 && data.data?.predictions) {
        setPredictions(data.data.predictions);
        setIsDropdownOpen(true);
      } else {
        setPredictions([]);
        setIsDropdownOpen(false);
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        console.error('Server API request timeout');
      } else {
        console.error('Server API failed:', error);
      }
      setPredictions([]);
      setIsDropdownOpen(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    onChange(inputValue);

    // Fetch predictions if input is sufficient
    if (inputValue.length > 2) {
      fetchPredictions(inputValue);
    } else {
      setPredictions([]);
      setIsDropdownOpen(false);
    }
  };

  const fetchCoordinates = async (address: string): Promise<{ lat: number; lng: number } | null> => {
    try {
      const serverUrl = API_BASE;
      const url = `${serverUrl}/api/places/geocode?address=${encodeURIComponent(address)}`;

      const response = await fetch(url, {
        signal: AbortSignal.timeout(5000), // 5 second timeout
        headers: {
          'Accept': 'application/json',
        },
      });

      if (!response.ok) {
        console.error('Geocoding API returned status:', response.status);
        return null;
      }

      const data = await response.json();

      if (data.statusCode === 200 && data.data?.coordinates) {
        return data.data.coordinates;
      } else {
        console.error('Geocoding failed:', data.message);
        return null;
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        console.error('Geocoding request timeout');
      } else {
        console.error('Geocoding failed:', error);
      }
      return null;
    }
  };

  const handleSelectPlace = async (prediction: AutocompletePrediction) => {
    const address = prediction.description;

    // Update the input field immediately for better UX
    if (inputRef.current) {
      inputRef.current.value = address;
    }

    setIsDropdownOpen(false);
    setPredictions([]);

    // Fetch coordinates for the selected address
    const coordinates = await fetchCoordinates(address);

    // Call onChange with both address and coordinates
    onChange(address, coordinates);

    // Force a re-render to ensure the input shows the new value
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.dispatchEvent(new Event('input', { bubbles: true }));
      }
    }, 0);
  };

  const handleClear = () => {
    onChange('');
    setPredictions([]);
    setIsDropdownOpen(false);
    if (onClear) {
      onClear();
    }
  };

  const handleClickOutside = (event: MouseEvent) => {
    // Don't close dropdown if clicking on suggestion buttons
    const target = event.target as Element;
    if (target.closest('button[data-suggestion="true"]')) {
      return;
    }

    if (inputRef.current && !inputRef.current.contains(event.target as Node)) {
      setIsDropdownOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className="relative">
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          id={id}
          value={value}
          onChange={handleInputChange}
          disabled={disabled}
          placeholder={placeholder}
          className={`
            block w-full px-3 py-2 pr-10 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500
            ${error ? 'border-red-300 focus:border-red-500' : 'border-gray-300 focus:border-blue-500'}
            ${disabled ? 'bg-gray-50 text-gray-500' : 'bg-white text-gray-900'}
            ${className}
          `}
          autoComplete="off"
        />

        {value && !disabled && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600 focus:outline-none focus:text-gray-600"
            aria-label="Clear address"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}

        {!value && !disabled && (
          <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </div>
        )}

        {isLoading && (
          <div className="absolute inset-y-0 right-0 flex items-center pr-3">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
          </div>
        )}
      </div>

      {error && (
        <p className="mt-1 text-sm text-red-600" role="alert">
          {error}
        </p>
      )}

      {isDropdownOpen && predictions.length > 0 && (
        <div
          className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto"
          style={{ zIndex: 9999, pointerEvents: 'auto' }}
        >
          {predictions.map((prediction, index) => (
            <button
              key={prediction.place_id}
              type="button"
              data-suggestion="true"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleSelectPlace(prediction);
              }}
              className="w-full px-3 py-2 text-left text-sm text-gray-900 hover:bg-gray-50 focus:bg-gray-50 focus:outline-none border-b border-gray-100 last:border-b-0 cursor-pointer"
              style={{ pointerEvents: 'auto' }}
            >
              <div className="flex items-start">
                <svg className="w-4 h-4 text-gray-400 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <div>
                  <div className="font-medium">{prediction.structured_formatting?.main_text}</div>
                  <div className="text-gray-500">{prediction.structured_formatting?.secondary_text}</div>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default GooglePlacesAutocomplete;